/**
 * Inspectors for edge-hosted segments: the alcove/edge-item editor (primary
 * edge segments, selection kinds alcove / alcoveCorner) and the line-segment
 * editor (segments on alcove walls, corner diagonals — selection kind lineSeg).
 */

import { css } from '@emotion/react';
import { EdgeName, FloorPlanCorner, FloorPlanEdgeSegment, LineEdgeRef, OuterWhich } from '@kaiizen/kai-floor-plan';
import {
  DOOR_SWING_OPTIONS,
  EDGE_ITEM_TYPES,
  LINE_EDGE_ITEM_TYPES,
  setDoorSwing,
  setEdgeItemKind,
  useFloorPlanEditorState,
} from '@kaiizen/kai-floor-plan-editor-core';
import { midtone } from '../../styles';
import { ChoiceField, CommitLengthField, CommitLengthInput } from './fields';

const CORNER_GRID_STYLE = css`
  label: CORNER_GRID_STYLE;
  display: grid; grid-template-columns: auto 1fr 1fr; gap: 6px 8px; align-items: center;
`;

/** The grid's row/column headers (the corner glyph, "dx", "dy"). */
const CORNER_GRID_HEADER_STYLE = css`
  label: CORNER_GRID_HEADER_STYLE;
  font-size: 11px; color: ${midtone}; text-align: right;
`;

function SwingSelect({ swing }: { swing: string }): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  return <ChoiceField id='al-swing' label='Swing' value={swing} options={DOOR_SWING_OPTIONS} onChange={choice => setDoorSwing(floorPlanEditorState, choice)} />;
}

function KindSelect({ kind, allowAlcove }: { kind: string; allowAlcove: boolean }): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  return (
    <ChoiceField
      id='al-kind'
      label='Type'
      value={kind}
      options={allowAlcove ? EDGE_ITEM_TYPES : LINE_EDGE_ITEM_TYPES}
      onChange={choice => setEdgeItemKind(floorPlanEditorState, choice)}
    />
  );
}

/** Opening height (blank = up to ceiling) + base rows, shared by all segment kinds. */
function OpeningFields({ edgeSegment, heightLabel, baseLabel }: {
  edgeSegment: FloorPlanEdgeSegment;
  heightLabel: string;
  baseLabel: string;
}): React.JSX.Element {
  return (
    <>
      <CommitLengthField label={heightLabel} setKey='opening-height' value={edgeSegment.openingHeight ?? null} />
      <CommitLengthField label={baseLabel} setKey='opening-base' value={edgeSegment.openingBase ?? 0} />
    </>
  );
}

function ChamferRow({ label, which, corner }: {
  label: string;
  which: OuterWhich;
  corner: FloorPlanCorner;
}): React.JSX.Element {
  return (
    <>
      <div css={CORNER_GRID_HEADER_STYLE}>{label}</div>
      <CommitLengthInput setKey={`alc-${which}-dx`} value={corner.dx} />
      <CommitLengthInput setKey={`alc-${which}-dy`} value={corner.dy} />
    </>
  );
}

function ChamferGrid({ edgeSegment }: { edgeSegment: FloorPlanEdgeSegment }): React.JSX.Element {
  return (
    <>
      <h2 style={{ marginTop: 12 }}>Outer corners (dx · dy)</h2>
      <div css={CORNER_GRID_STYLE} data-testid='corner-grid'>
        <div css={CORNER_GRID_HEADER_STYLE}></div>
        <div css={CORNER_GRID_HEADER_STYLE}>dx</div>
        <div css={CORNER_GRID_HEADER_STYLE}>dy</div>
        <ChamferRow label='outer ◤' which='backLeftCorner' corner={edgeSegment.backLeftCorner ?? { dx: 0, dy: 0 }} />
        <ChamferRow label='outer ◥' which='backRightCorner' corner={edgeSegment.backRightCorner ?? { dx: 0, dy: 0 }} />
      </div>
    </>
  );
}

/** Editor for a primary-edge segment (selection kind alcove / alcoveCorner). */
export function AlcoveInspector({ edgeSegment, edge, index }: {
  edgeSegment: FloorPlanEdgeSegment;
  edge: EdgeName;
  index: number;
}): React.JSX.Element {
  return (
    <>
      <h2>Edge item · {edge} #{index + 1}</h2>
      <KindSelect kind={edgeSegment.edgeSegmentType} allowAlcove />
      <CommitLengthField label='Position' setKey='al-position' value={edgeSegment.position} />
      <CommitLengthField label='Width' setKey='al-width' value={edgeSegment.width} />
      {edgeSegment.edgeSegmentType === 'alcove'
        ? (
          <>
            <CommitLengthField label='Depth' setKey='al-depth' value={edgeSegment.depth ?? 0} />
            <OpeningFields edgeSegment={edgeSegment} heightLabel='Height' baseLabel='Base (off floor)' />
            <ChamferGrid edgeSegment={edgeSegment} />
          </>
        )
        : (
          <>
            {edgeSegment.edgeSegmentType === 'door' && <SwingSelect swing={edgeSegment.swing ?? 'inLeft'} />}
            <OpeningFields edgeSegment={edgeSegment} heightLabel='Opening height' baseLabel='Opening base' />
          </>
        )}
    </>
  );
}

/** Editor for a segment on any line edge (selection kind lineSeg). */
export function LineSegInspector({ edgeSegment, lineEdgeRef }: {
  edgeSegment: FloorPlanEdgeSegment;
  lineEdgeRef: LineEdgeRef;
}): React.JSX.Element {
  const where = lineEdgeRef.k === 'pe'
    ? `${lineEdgeRef.edge}`
    : lineEdgeRef.k === 'aw'
    ? `${lineEdgeRef.wall} wall`
    : lineEdgeRef.k === 'rc'
    ? 'corner diagonal'
    : 'outer corner diagonal';
  return (
    <>
      <h2>Segment · {where}</h2>
      <KindSelect kind={edgeSegment.edgeSegmentType} allowAlcove={lineEdgeRef.k === 'pe'} />
      <CommitLengthField label='Position' setKey='seg-position' value={edgeSegment.position} />
      <CommitLengthField label='Width' setKey='seg-width' value={edgeSegment.width} />
      {edgeSegment.edgeSegmentType === 'door' && <SwingSelect swing={edgeSegment.swing ?? 'inLeft'} />}
      <OpeningFields edgeSegment={edgeSegment} heightLabel='Opening height' baseLabel='Opening base' />
    </>
  );
}
